import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Package,
  Euro,
  TrendingUp,
  CreditCard,
  Globe,
  BarChart3,
  Heart,
  AlertCircle,
  Check,
} from "lucide-react";
import { OrderStatusBadge } from "./OrderActions";
import { AnalyticsAdvanced } from "./AnalyticsAdvanced";
import { EmailABStatsWidget } from "./EmailABStatsWidget";
import { CartRecoveryWidget } from "./CartRecoveryWidget";
import { VisitorStatsWidget } from "./VisitorStatsWidget";
import { TelegramChannelWidget } from "./TelegramChannelWidget";
import { PreordersWidget } from "./PreordersWidget";
import { RevenueByZoneWidget } from "./RevenueByZoneWidget";
import { CouponsOverviewWidget } from "./CouponsOverviewWidget";
import { SyncStatusWidget } from "./SyncStatusWidget";
import { TopCustomersWidget } from "./TopCustomersWidget";
import { TopReferrersWidget } from "./TopReferrersWidget";
import { CustomPaymentLinkWidget } from "./CustomPaymentLinkWidget";
import { AOVWidget } from "./AOVWidget";
import { ExpressMetricWidget } from "./ExpressMetricWidget";
import { AiChatStatsWidget } from "./AiChatStatsWidget";
import { WeeklyDigestWidget } from "./WeeklyDigestWidget";
import { SuspiciousOrdersWidget } from "./SuspiciousOrdersWidget";
import { EbayDuplicatesWidget } from "./EbayDuplicatesWidget";
import { OrderAttemptsWidget } from "./OrderAttemptsWidget";

/**
 * Dashboard widget cluster — extracted from AdminApp.jsx during Task A Phase 4.
 * Pure presentational component: receives data + helpers as props.
 */
export const DashboardView = ({
  loading,
  articles,
  orders,
  newsletterSubscriptions,
  revenueAnalytics,
  siteAnalytics,
  wishlistAnalytics,
  pendingOrdersDesc,
  recent5OrdersDesc,
  formatPrice,
  formatDate,
  getPaymentMethodLabel,
  setCurrentView,
}) => {
  return (
    <>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">Dashboard Generale</h2>
        <p className="text-gray-400">Panoramica completa dell'e-commerce</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Caricamento...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Totale Articoli</p>
                    <p className="text-3xl font-bold text-gray-900">{articles.length}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  In stock: {articles.filter((a) => a.quantity_stock > 0).length}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Totale Ordini</p>
                    <p className="text-3xl font-bold text-gray-900">{orders.length}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <Package className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Pendenti: {orders.filter((o) => o.status === "pending").length}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Newsletter</p>
                    <p className="text-3xl font-bold text-gray-900">{newsletterSubscriptions.length}</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-full">
                    <Package className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Iscritti attivi</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Valore Ordini</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatPrice(orders.reduce((sum, o) => (
                        (o.status || "").toLowerCase() === "cancelled"
                          ? sum
                          : sum + (o.total_amount || 0)
                      ), 0))}
                    </p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded-full">
                    <Euro className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Totale vendite</p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Analytics */}
          {revenueAnalytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Revenue */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Incassi per Mese
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueAnalytics.monthly.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nessun dato disponibile</p>
                  ) : (
                    <div className="space-y-2">
                      {revenueAnalytics.monthly.slice(-12).map((m) => {
                        const maxRevenue = Math.max(...revenueAnalytics.monthly.map((x) => x.revenue));
                        const pct = maxRevenue > 0 ? (m.revenue / maxRevenue) * 100 : 0;
                        const [year, month] = m.month.split("-");
                        const monthNames = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
                        const label = `${monthNames[parseInt(month) - 1]} ${year}`;
                        return (
                          <div key={m.month} data-testid={`monthly-revenue-${m.month}`}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-700 font-medium">{label}</span>
                              <span className="text-gray-900 font-semibold">
                                {formatPrice(m.revenue)}{" "}
                                <span className="text-gray-400 font-normal">({m.orders_count} ord.)</span>
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(pct, 3)}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Revenue by Payment Method */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    Incassi per Metodo di Pagamento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueAnalytics.by_payment_method.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nessun dato disponibile</p>
                  ) : (
                    <div className="space-y-4">
                      {revenueAnalytics.by_payment_method.map((p) => {
                        const pct = revenueAnalytics.total_revenue > 0
                          ? ((p.revenue / revenueAnalytics.total_revenue) * 100).toFixed(1)
                          : 0;
                        const colors = {
                          stripe: "from-indigo-500 to-indigo-600",
                          card: "from-indigo-500 to-indigo-600",
                          paypal: "from-blue-400 to-blue-500",
                          transfer: "from-emerald-500 to-emerald-600",
                          cash: "from-amber-500 to-amber-600",
                          ebay: "from-red-500 via-yellow-400 to-blue-500",
                          satispay: "from-red-500 to-red-600",
                          klarna: "from-pink-400 to-pink-500",
                          link: "from-green-400 to-teal-500",
                          apple_pay: "from-gray-800 to-gray-900",
                          google_pay: "from-blue-500 to-blue-600",
                        };
                        // Colored inline logo/badge for each payment method
                        const logos = {
                          stripe: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-indigo-600 text-white font-bold text-[10px]">stripe</span>,
                          card: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-indigo-600 text-white font-bold text-[10px]">CARD</span>,
                          paypal: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-[#003087] text-white font-bold text-[10px]"><span className="text-[#009cde]">Pay</span>Pal</span>,
                          transfer: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-emerald-600 text-white text-sm">🏦</span>,
                          cash: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-amber-500 text-white text-sm">💵</span>,
                          ebay: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-white border border-gray-200 font-bold text-[11px]"><span className="text-[#e53238]">e</span><span className="text-[#0064d2]">b</span><span className="text-[#f5af02]">a</span><span className="text-[#86b817]">y</span></span>,
                          satispay: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-[#FF3625] text-white font-bold text-[10px]">S</span>,
                          klarna: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-[#FFA8C5] text-black font-bold text-[10px]">K.</span>,
                          link: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-[#00D66F] text-white font-bold text-[10px]">Link</span>,
                          apple_pay: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-black text-white text-sm"></span>,
                          google_pay: <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-white border border-gray-300 font-bold text-[10px]"><span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">P</span><span className="text-[#FBBC04]">a</span><span className="text-[#34A853]">y</span></span>,
                        };
                        const barColor = colors[p.method] || "from-gray-400 to-gray-500";
                        const logo = logos[p.method] || <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-gray-400 text-white text-xs">?</span>;
                        return (
                          <div key={p.method} data-testid={`payment-method-${p.method}`}>
                            <div className="flex justify-between text-sm mb-1 items-center">
                              <span className="text-gray-700 font-medium flex items-center gap-2">
                                {logo}
                                {p.label}
                              </span>
                              <span className="text-gray-900 font-semibold">
                                {formatPrice(p.revenue)}{" "}
                                <span className="text-gray-400 font-normal">({pct}%)</span>
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3">
                              <div
                                className={`bg-gradient-to-r ${barColor} h-3 rounded-full transition-all duration-500`}
                                style={{ width: `${Math.max(parseFloat(pct), 3)}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">{p.orders_count} ordini</p>
                          </div>
                        );
                      })}
                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex justify-between font-semibold text-gray-900">
                          <span>Totale</span>
                          <span>{formatPrice(revenueAnalytics.total_revenue)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Site Analytics - Google Analytics */}
          {siteAnalytics && !siteAnalytics.error && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-purple-600" />
                    Visite per Nazione
                    <span className="text-xs font-normal text-gray-400 ml-auto">{siteAnalytics.period}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {siteAnalytics.visitors_by_country.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Globe className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>Nessun dato disponibile ancora.</p>
                      <p className="text-xs mt-1">I dati appariranno dopo il deploy in produzione.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {siteAnalytics.visitors_by_country.map((c) => {
                        const maxVisitors = Math.max(...siteAnalytics.visitors_by_country.map((x) => x.visitors));
                        const pct = maxVisitors > 0 ? (c.visitors / maxVisitors) * 100 : 0;
                        return (
                          <div key={c.country} data-testid={`country-visitors-${c.country}`}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-700 font-medium">{c.country}</span>
                              <span className="text-gray-900 font-semibold">
                                {c.visitors} <span className="text-gray-400 font-normal">({c.pageviews} pagine)</span>
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3">
                              <div
                                className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(pct, 3)}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex justify-between font-semibold text-gray-900">
                          <span>Totale visitatori</span>
                          <span>{siteAnalytics.total_visitors}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-teal-600" />
                    Trend Visite (Ultimi 7 giorni)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!siteAnalytics.daily_trend || siteAnalytics.daily_trend.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>Nessun dato disponibile ancora.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {siteAnalytics.daily_trend.map((d) => {
                        const maxDayVisitors = Math.max(...siteAnalytics.daily_trend.map((x) => x.visitors), 1);
                        const pct = (d.visitors / maxDayVisitors) * 100;
                        return (
                          <div key={d.date} data-testid={`daily-trend-${d.date}`}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-700 font-medium">{d.date}</span>
                              <span className="text-gray-900 font-semibold">
                                {d.visitors} visitatori{" "}
                                <span className="text-gray-400 font-normal">({d.pageviews} pagine)</span>
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-3">
                              <div
                                className="bg-gradient-to-r from-teal-400 to-teal-600 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(pct, 3)}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* GA Error message */}
          {siteAnalytics && siteAnalytics.error && (
            <Card>
              <CardContent className="py-6 text-center">
                <Globe className="w-10 h-10 mx-auto mb-2 text-amber-400" />
                <p className="text-gray-500 text-sm">Google Analytics: dati non ancora disponibili.</p>
                <p className="text-xs text-gray-400 mt-1">
                  Assicurati che il service account abbia accesso alla proprietà GA4 e che il tag sia attivo sul sito.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Wishlist Analytics - Demand Insights */}
          {wishlistAnalytics &&
            (wishlistAnalytics.top_wished?.length > 0 || wishlistAnalytics.out_of_stock_wished?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="wishlist-analytics-section">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-pink-500" />
                      Articoli più desiderati
                      <span className="ml-auto text-xs font-normal text-gray-400">
                        {wishlistAnalytics.total_wishes} desideri · {wishlistAnalytics.unique_users} clienti
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {wishlistAnalytics.top_wished.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Nessun articolo desiderato ancora.</p>
                    ) : (
                      <div className="space-y-2">
                        {wishlistAnalytics.top_wished.slice(0, 8).map((a) => {
                          const maxW = Math.max(...wishlistAnalytics.top_wished.map((x) => x.wishes), 1);
                          const pct = (a.wishes / maxW) * 100;
                          const outOfStock = (a.quantity_stock || 0) <= 0 && a.availability_type !== "preorder";
                          return (
                            <div key={a.article_id} data-testid={`top-wished-${a.article_id}`}>
                              <div className="flex justify-between text-sm mb-1 gap-2">
                                <span className="text-gray-700 font-medium truncate" title={a.title}>
                                  {a.title}
                                </span>
                                <span className="text-gray-900 font-semibold whitespace-nowrap">
                                  {a.wishes} ❤
                                  {outOfStock && (
                                    <Badge className="ml-2 bg-red-100 text-red-700 hover:bg-red-100">Esaurito</Badge>
                                  )}
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2.5">
                                <div
                                  className="bg-gradient-to-r from-pink-400 to-pink-600 h-2.5 rounded-full transition-all duration-500"
                                  style={{ width: `${Math.max(pct, 3)}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      Priorità riassortimento
                      <span className="ml-auto text-xs font-normal text-gray-400">Esauriti ma desiderati</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {wishlistAnalytics.out_of_stock_wished.length === 0 ? (
                      <div className="text-center py-6 text-gray-500">
                        <Check className="w-10 h-10 mx-auto mb-2 text-green-400" />
                        <p>Nessun articolo desiderato è esaurito.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {wishlistAnalytics.out_of_stock_wished.slice(0, 8).map((a) => (
                          <div
                            key={a.article_id}
                            data-testid={`out-of-stock-wished-${a.article_id}`}
                            className="flex items-center gap-3 p-2 rounded-lg bg-amber-50 border border-amber-100"
                          >
                            {a.image && <img src={a.image} alt={a.title} className="w-10 h-12 object-cover rounded" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={a.title}>
                                {a.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {a.category} · {formatPrice(a.price)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-amber-600">{a.wishes}</p>
                              <p className="text-xs text-gray-500">desideri</p>
                            </div>
                          </div>
                        ))}
                        <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                          Riordinare questi articoli per soddisfare la domanda → invio automatico email "Tornato disponibile".
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

          {/* Recent Orders - New */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>🆕 Nuovi Ordini</CardTitle>
                {orders.filter((o) => o.status === "pending").length > 0 && (
                  <Badge className="bg-red-500">
                    {orders.filter((o) => o.status === "pending").length} in attesa
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {pendingOrdersDesc.length === 0 ? (
                <p className="text-gray-600 text-center py-6">✅ Nessun nuovo ordine da processare</p>
              ) : (
                <div className="space-y-3">
                  {pendingOrdersDesc.map((order) => (
                    <div
                      key={order.id}
                      className="flex justify-between items-center py-3 px-4 border-l-4 border-red-500 bg-red-50 rounded"
                    >
                      <div>
                        <p className="font-medium text-red-700">#{order.id.slice(-8)}</p>
                        <p className="text-sm text-gray-600">{order.customer_info.name}</p>
                        <p className="text-xs text-gray-500">{formatDate(order.order_date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-700">{formatPrice(order.total_amount)}</p>
                        <p className="text-xs text-gray-600">{getPaymentMethodLabel(order.payment_method)}</p>
                        <Button
                          size="sm"
                          className="mt-2"
                          onClick={() => setCurrentView("orders")}
                          data-testid={`manage-pending-${order.id}`}
                        >
                          Gestisci →
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Recent Orders */}
          <Card>
            <CardHeader>
              <CardTitle>Tutti gli Ordini Recenti</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <p className="text-gray-600 text-center py-6">Nessun ordine ancora</p>
              ) : (
                <div className="space-y-3">
                  {recent5OrdersDesc.map((order) => (
                    <div
                      key={order.id}
                      className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                    >
                      <div>
                        <p className="font-medium">#{order.id.slice(-8)}</p>
                        <p className="text-sm text-gray-600">{order.customer_info.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(order.total_amount)}</p>
                        <OrderStatusBadge status={order.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Categories Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Articoli per Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              {articles.length === 0 ? (
                <p className="text-gray-600 text-center py-6">Nessun articolo</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(
                    articles.reduce((acc, article) => {
                      const cat = article.category || "Senza categoria";
                      acc[cat] = (acc[cat] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([category, count]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-gray-700">{category}</span>
                      <Badge variant="secondary">{count} articoli</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Avanzati — visitatori, pagine, referrer, abandoned */}
      <AnalyticsAdvanced formatPrice={formatPrice} />

      {/* Email A/B Test stats (delivery_confirmation + save_cart campaigns) */}
      <div className="mt-6 space-y-6">
        <EbayDuplicatesWidget />
        <OrderAttemptsWidget />
        <SuspiciousOrdersWidget />
        <AOVWidget />
        <ExpressMetricWidget />
        <AiChatStatsWidget />
        <WeeklyDigestWidget />
        <CustomPaymentLinkWidget />
        <SyncStatusWidget />
        <TopCustomersWidget />
        <TopReferrersWidget />
        <VisitorStatsWidget />
        <RevenueByZoneWidget />
        <PreordersWidget />
        <TelegramChannelWidget />

        <EmailABStatsWidget />

        <CartRecoveryWidget />
        <CouponsOverviewWidget />
      </div>
    </>
  );
};
